import React, { useState, useEffect } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Container, Typography, Chip, TextField, MenuItem, TablePagination, Avatar,
  Stack, IconButton, Tooltip,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { collection, onSnapshot, query, orderBy, limit, where, Timestamp } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";

const ACTION_COLORS = {
  suspend: "warning",
  unsuspend: "success",
  delete: "error",
  "password-reset": "info",
  "mass-notification": "secondary",
  "sec-letter": "primary",
  "gift-add": "success",
  "gift-edit": "warning",
  "gift-delete": "error",
  "content-remove": "error",
  "report-dismiss": "default",
  login: "success",
  default: "default",
};

const ACTION_ICONS = {
  suspend: "eva:slash-fill",
  unsuspend: "eva:checkmark-circle-fill",
  delete: "fe:trash",
  "password-reset": "eva:lock-fill",
  "mass-notification": "ant-design:message-filled",
  "sec-letter": "eva:file-text-fill",
  "gift-add": "eva:gift-fill",
  "gift-edit": "eva:edit-fill",
  "gift-delete": "fe:trash",
  "content-remove": "eva:alert-triangle-fill",
  "report-dismiss": "eva:close-circle-fill",
  login: "eva:log-in-fill",
  default: "eva:activity-fill",
};

const FILTER_OPTIONS = [
  { value: "all", label: "All Actions" },
  { value: "suspend", label: "Suspensions" },
  { value: "delete", label: "Deletions" },
  { value: "mass-notification", label: "Notifications" },
  { value: "sec-letter", label: "SEC Letters" },
  { value: "content-remove", label: "Content Removed" },
  { value: "login", label: "Logins" },
];

const DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "all", label: "All Time" },
];

// Helper to log actions — import and call this from other modules
export const logAdminAction = async (action, targetName, details = {}, adminEmail = "") => {
  try {
    const { addDoc, serverTimestamp } = await import("firebase/firestore");
    const { db } = await import("../database/db");
    const { collection } = await import("firebase/firestore");
    await addDoc(collection(db, "AuditLog"), {
      action,
      targetName,
      details,
      adminEmail,
      timestamp: serverTimestamp(),
    });
  } catch (_) {}
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("7days");
  const [adminFilter, setAdminFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "AuditLog"), orderBy("timestamp", "desc"), limit(500));
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const getDateCutoff = () => {
    const now = new Date();
    if (dateFilter === "today") { now.setHours(0,0,0,0); return now; }
    if (dateFilter === "7days") { now.setDate(now.getDate() - 7); return now; }
    if (dateFilter === "30days") { now.setDate(now.getDate() - 30); return now; }
    return null;
  };

  const filtered = logs.filter((log) => {
    if (actionFilter !== "all" && log.action !== actionFilter) return false;
    if (adminFilter && !(log.adminEmail || "").toLowerCase().includes(adminFilter.toLowerCase())) return false;
    const cutoff = getDateCutoff();
    if (cutoff && log.timestamp) {
      const ts = log.timestamp.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
      if (ts < cutoff) return false;
    }
    return true;
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatTime = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  };

  return (
    <Page title="Audit Log | Fire Investing Admin">
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">
            Complete history of all admin actions — {filtered.length} entries
          </Typography>
        </Box>

        {/* Filters */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
          <TextField select size="small" label="Action Type" value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 160 }}>
            {FILTER_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Date Range" value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 140 }}>
            {DATE_FILTERS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Filter by Admin Email" value={adminFilter}
            onChange={(e) => { setAdminFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 220 }}
            InputProps={{ startAdornment: <Icon icon="eva:search-fill" style={{ marginRight: 6, opacity: 0.5 }} /> }}
          />
        </Stack>

        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          <Scrollbar>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Timestamp</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Chip
                          icon={<Icon icon={ACTION_ICONS[log.action] || ACTION_ICONS.default} width={14} />}
                          label={log.action || "unknown"}
                          size="small"
                          color={ACTION_COLORS[log.action] || ACTION_COLORS.default}
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {log.targetName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {log.adminEmail || "system"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {log.details ? JSON.stringify(log.details) : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatTime(log.timestamp)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          {loading ? "Loading audit log..." : "No entries match your filters"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Card>
      </Container>
    </Page>
  );
}
